import AvatarDetails from "@/components/AvatarDetails";
import EmptyState from "@/components/EmptyState";
import Table, { Props, Row, buildCopyTextColumn, buildExpandableCountColumn } from "@/components/Table";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import { toDateTimeFormat } from "@/lib/dateHelpers";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import { AuditEntry } from "@/pages/sections/types";
import { Link } from "@/router";
import { AUDIT_CATEGORY_LABELS } from "../categories";
import { ColumnDef, Row as TanstackRow } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";

type ChangesetRow = AuditEntry["changeset"][number];

const changesetColumns: ColumnDef<ChangesetRow>[] = [
  { accessorKey: "typeOfData", header: "Type of Data", enableSorting: false },
  { accessorKey: "oldValue", header: "Old Value", enableSorting: false },
  { accessorKey: "newValue", header: "New Value", enableSorting: false }
];

function renderExpandedRow({ row }: { row: TanstackRow<AuditEntry> }) {
  const changeset = row.original.changeset ?? [];

  return <TableDetailsPanel title={`Changes (${changeset.length})`} data={changeset} columns={changesetColumns} />;
}

const firstSticky = <T,>(hasData: boolean, [column, ...rest]: ColumnDef<T, unknown>[]): ColumnDef<T, unknown>[] => [
  {
    ...column,
    meta: { ...column.meta, className: getStickyColumnClasses(hasData) }
  },
  ...rest
];

function pgaIdText(row: AuditEntry) {
  return row.person?.pgaId ?? row.pgaId ?? "";
}

const columnDefs: Record<string, ColumnDef<AuditEntry, unknown>> = {
  itemModified: {
    id: "itemModified",
    header: "Item Modified",
    enableSorting: false,
    cell: ({ row }) => {
      const { person, profilePhotoUrl, itemName } = row.original;
      return (
        <AvatarDetails
          picture={person?.profilePhoto ?? profilePhotoUrl ?? undefined}
          title={person?.displayName || itemName || ""}
          text={person?.latestProgram?.programType?.name || ""}
        />
      );
    },
    meta: { headerClassName: "hide-header-in-column" }
  },
  pgaId: buildCopyTextColumn<AuditEntry>({
    accessorKey: "pgaId",
    header: "PGA ID",
    copyFieldLabel: "PGA ID",
    getText: pgaIdText,
    shouldShowCopyButton: (row) => !!pgaIdText(row),
    enableSorting: false
  }),
  changeType: {
    accessorKey: "category",
    header: "Change Type",
    enableSorting: false,
    cell: ({ row }) => {
      const { category } = row.original;
      return category ? AUDIT_CATEGORY_LABELS[category] : null;
    }
  },
  description: {
    accessorKey: "description",
    header: "Description",
    enableSorting: false
  },
  changedAt: {
    accessorKey: "changedAt",
    header: "Timestamp",
    enableSorting: false,
    cell: ({ row }) => toDateTimeFormat(row.original.changedAt, "LLL dd, yyyy h:mm a (ZZZZ)")
  },
  whodunnit: {
    accessorKey: "whodunnit",
    header: "Modified By",
    enableSorting: false,
    cell: ({
      row: {
        original: { whodunnit }
      }
    }) => {
      switch (whodunnit?.__typename) {
        case "BackofficePerson":
          return (
            <Link to="/people/:pgaId/person" params={{ pgaId: whodunnit.id }}>
              {whodunnit.displayName}
            </Link>
          );
        case "BackofficeUser":
          return <a href={`mailto:${whodunnit.email}`}>{whodunnit.name}</a>;
        case "BackofficeAdminUser":
          return <a href={`mailto:${whodunnit.email}`}>{whodunnit.email}</a>;
        default:
          return null;
      }
    }
  },
  changeset: buildExpandableCountColumn<AuditEntry>({
    accessorKey: "changeset",
    header: "Changes",
    getCount: (row) => row.changeset?.length || 0
  })
};

type ColumnName = keyof typeof columnDefs;

const defaultColumns: ColumnName[] = [
  "itemModified",
  "pgaId",
  "changeType",
  "description",
  "changedAt",
  "whodunnit",
  "changeset"
];

type AuditLogTableProps = Omit<
  Props<AuditEntry>,
  "columns" | "sortingState" | "renderExpandedRow" | "onClickRow" | "getRowLink"
> & { columns?: ColumnName[] };

export default function AuditLogTable({
  columns: columnNames,
  hasActiveFilters,
  data,
  renderFilteredEmptyState,
  renderNoDataEmptyState,
  ...props
}: AuditLogTableProps) {
  const navigate = useNavigate();
  const columns = (columnNames || defaultColumns).map((name) => columnDefs[name]);

  const onClickRow = (row: Row<AuditEntry>) => {
    const linkPath = row.original.linkPath;
    if (!linkPath) return;
    navigate(linkPath);
  };

  return (
    <Table
      renderExpandedRow={renderExpandedRow}
      columns={firstSticky(!!data?.length, columns)}
      hasActiveFilters={hasActiveFilters}
      getRowCanExpand={(row) => (row.original.changeset?.length || 0) > 0}
      onClickRow={onClickRow}
      renderNoDataEmptyState={
        renderNoDataEmptyState ??
        (() => <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="today" />)
      }
      renderFilteredEmptyState={
        renderFilteredEmptyState ??
        (() => <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="search" />)
      }
      data={data}
      {...props}
    />
  );
}
